import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, tasksAPI, usersAPI } from '../api/api';
import {
  clearAuthSession,
  getStoredToken,
  getStoredUser,
  storeAuthSession,
} from '../api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [wiki, setWiki] = useState([]);
  const [shifts, setShifts] = useState([]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      if (response && response.users) {
        setUsers(response.users);
      }
    } catch (error) {
      setUsers([]);
    }
  };

  const loadTasks = async (currentUser = user) => {
    if (!currentUser) return;
    
    try {
      const response = await tasksAPI.getAll();
      
      if (response?.tasks && Array.isArray(response.tasks)) {
        const formattedTasks = response.tasks.map(task => ({
          id: task.id,
          title: task.title,
          description: task.description,
          priority: task.priority,
          status: task.status,
          dueDate: task.dueDate,
          createdAt: task.createdAt || task.created_at,
          updatedAt: task.updatedAt || task.updated_at,
          department: task.department || '',
          shift_type: task.shift_type || task.shiftType,
          creator_id: task.creator_id,
          creator: task.creator
        }));
        
        setTasks(formattedTasks);
      } else {
        setTasks([]);
      }
    } catch (error) {
      setTasks([]);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = getStoredUser();
        const token = getStoredToken();

        if (user && token) {
          setUser(user);
          await loadTasks(user);
          await loadUsers();
        } else {
          clearAuthSession();
        }
      } catch (error) {
        clearAuthSession();
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (user && !loading) {
      loadTasks(user);
    }
  }, [user, loading]);

  const login = async (username, password, spaceId) => {
    try {
      const response = await authAPI.login(username, password, spaceId);
      
      if (response.user && response.token) {
        storeAuthSession({
          user: response.user,
          token: response.token,
        });
        setUser(response.user);
        await loadTasks(response.user);
        await loadUsers();
        
        return { success: true };
      } else if (response.message) {
        return { 
          success: false, 
          message: response.message 
        };
      } else {
        return { 
          success: false, 
          message: 'Login failed - invalid response from server' 
        };
      }
    } catch (error) {
      let userMessage = error.message || 'Network error';
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        userMessage = 'Неверное имя пользователя или пароль';
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        userMessage = 'Сервис авторизации не найден';
      } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
        userMessage = 'Имя пользователя и пароль обязательны';
      } else if (error.message.includes('500')) {
        userMessage = 'Ошибка сервера - попробуйте позже';
      }
      
      return { 
        success: false, 
        message: userMessage 
      };
    }
  };

  const logout = () => {
    clearAuthSession();
    setUser(null);
    setTasks([]);
    setUsers([]);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isManager = () => {
    return user?.role === 'manager';
  };

  const isEmployee = () => {
    return user?.role === 'employee';
  };

  const addTask = async (task) => {
    try {
      if (!task.title) {
        throw new Error('Title is required');
      }
      if (!task.department) {
        throw new Error('Department is required');
      }
      
      const taskData = {
        title: task.title,
        description: task.description || '',
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        due_date: task.dueDate || task.due_date,
        department: task.department,
        shift_type: task.shift_type || task.shiftType || 'day',
        creator_id: user.id,
        userId: user.id
      };
      
      const response = await tasksAPI.create(taskData);
      
      if (response.task) {
        await loadTasks(user);
        return response.task;
      } else {
        throw new Error('No task in API response');
      }
    } catch (error) {
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const updateData = { ...updates };
      
      if (updates.dueDate) {
        updateData.due_date = updates.dueDate;
        delete updateData.dueDate;
      }
      
      delete updateData.assignee;
      delete updateData.creator;
      delete updateData.createdAt;
      delete updateData.updatedAt;
      
      updateData.updated_at = new Date().toISOString();
      
      // Оптимистичное обновление
      setTasks(prev => {
        const newTasks = prev.map(task => {
          if (task.id === taskId) {
            const updatedTask = {
              ...task,
              ...updateData,
              dueDate: updateData.due_date,
              updated_at: updateData.updated_at
            };
            return updatedTask;
          }
          return task;
        });
        return newTasks;
      });
      
      const response = await tasksAPI.update(taskId, updateData);
      return response;
      
    } catch (error) {
      // В случае ошибки перезагружаем актуальные данные
      await loadTasks(user);
      throw error;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await tasksAPI.delete(taskId);
      await loadTasks(user);
    } catch (error) {
      throw error;
    }
  };

  const addWikiPage = (page) => {
    const newPage = {
      ...page,
      id: Math.max(0, ...wiki.map(p => p.id)) + 1,
      author: { username: user?.username || 'User' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setWiki(prev => [...prev, newPage]);
    return newPage;
  };

  const updateWikiPage = (pageId, updates) => {
    setWiki(prev => prev.map(page => 
      page.id === pageId 
        ? { ...page, ...updates, updatedAt: new Date().toISOString() } 
        : page
    ));
  };

  const deleteWikiPage = (pageId) => {
    setWiki(prev => prev.filter(page => page.id !== pageId));
  };

  const addShift = (shift) => {
    const newShift = {
      ...shift,
      id: Math.max(0, ...shifts.map(s => s.id)) + 1,
      user_name: user?.username,
      status: 'scheduled',
      created_at: new Date().toISOString()
    };
    setShifts(prev => [...prev, newShift]);
    return newShift;
  };

  const updateShift = (shiftId, updates) => {
    setShifts(prev => prev.map(shift => 
      shift.id === shiftId ? { ...shift, ...updates } : shift
    ));
  };

  const deleteShift = (shiftId) => {
    setShifts(prev => prev.filter(shift => shift.id !== shiftId));
  };

  const value = {
    user,
    loading,
    users,
    login,
    logout,
    isAdmin,
    isManager,
    isEmployee,
    tasks,
    addTask,
    updateTask,
    deleteTask,
    wiki,
    addWikiPage,
    updateWikiPage,
    deleteWikiPage,
    shifts,
    addShift,
    updateShift,
    deleteShift,
    reloadTasks: () => loadTasks(user),
    reloadUsers: loadUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
